import { AddIcon } from '@systemeio/ui/icons'
import { FormInputNumber } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { AffiliateCommissionInterface } from 'modules/funnels/funnel/components/step-configuration-tab/affiliate-commission/types/affiliate-commission-interface'
import RemoveIcon from 'modules/funnels/funnel/icons/remove-icon'
import React, { useEffect, useState } from 'react'
import { useActOnBlur } from 'shared/hooks/use-act-on-blur'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'

interface AffiliateCommissionProps extends AffiliateCommissionInterface {
  onSave: (data: AffiliateCommissionInterface) => Promise<void>
}

const defaultValues: AffiliateCommissionInterface = { affiliateCommission: 0, payoutDelay: 0 }

function AffiliateCommission({
  affiliateCommission,
  payoutDelay,
  onSave,
}: AffiliateCommissionProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useLocoTranslation()
  const [tempState, setTempState] = useState(defaultValues)

  const handleError = (error: Error) => {
    if (error instanceof BadRequest) {
      const errors = error.errors.fields
      affiliateCommissionActor.setStatus(errors.affiliateCommission?.join(', '))
      payoutDelayActor.setStatus(errors.payoutDelay?.join(', '))
    }
  }

  const affiliateCommissionActor = useActOnBlur<
    AffiliateCommissionInterface['affiliateCommission']
  >(() => ({
    action: async value => {
      await onSave({
        ...tempState,
        affiliateCommission: value,
      })
    },
    onError: handleError,
  }))

  const payoutDelayActor = useActOnBlur<AffiliateCommissionInterface['payoutDelay']>(() => ({
    action: async value => {
      await onSave({
        ...tempState,
        payoutDelay: value,
      })
    },
    onError: handleError,
  }))

  useEffect(() => {
    if (affiliateCommission || payoutDelay) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
    setTempState({
      affiliateCommission,
      payoutDelay,
    })
  }, [affiliateCommission, payoutDelay])

  return (
    <>
      <div className="flex items-center">
        {isOpen ? (
          <RemoveIcon
            className={`fill-brand-primary mr-3 h-[18px] w-[18px] cursor-pointer`}
            onClick={() => setIsOpen(false)}
          />
        ) : (
          <AddIcon
            className={`fill-brand-primary mr-3 h-[18px] w-[18px] cursor-pointer`}
            onClick={() => setIsOpen(true)}
          />
        )}
        <span className={'text-text-primary text-sm font-normal lg:text-[15px]'}>
          {t('dashboard.funnel.configuration.offer.affiliate_commission')}
        </span>
        {isOpen && <span className="text-danger">*</span>}
      </div>
      {isOpen && (
        <div className="flex flex-col gap-4 xl:flex-row xl:gap-10">
          <FormInputNumber
            label={t('dashboard.funnel.configuration.offer.affiliate_commission')}
            type={'integer'}
            labelClassName={'lg:text-[15px] font-medium text-sm text-text-primary'}
            onChange={value =>
              setTempState(prev => ({
                ...prev,
                affiliateCommission: value,
              }))
            }
            addonAfter={
              !affiliateCommissionActor.isInProgress ? '%' : affiliateCommissionActor.statusIcon
            }
            onBlur={(_, processedNumber) => affiliateCommissionActor.handleBlur(processedNumber)}
            value={tempState.affiliateCommission}
            required
            max={100}
            error={affiliateCommissionActor.error}
          />
          <FormInputNumber
            label={t('dashboard.funnel.configuration.offer.payout_delay')}
            type={'integer'}
            labelClassName={'lg:text-[15px] font-medium text-sm text-text-primary'}
            onChange={value =>
              setTempState(prev => ({
                ...prev,
                payoutDelay: value,
              }))
            }
            addonAfter={
              !payoutDelayActor.isInProgress
                ? tempState.payoutDelay || 0 > 1
                  ? t('dashboard.transactions.sub_interval.days')
                  : t('dashboard.transactions.sub_interval.day')
                : payoutDelayActor.statusIcon
            }
            onBlur={(_, processedNumber) => payoutDelayActor.handleBlur(processedNumber)}
            max={100}
            value={tempState.payoutDelay}
            required
            error={payoutDelayActor.error}
          />
        </div>
      )}
    </>
  )
}

export default AffiliateCommission
